import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Plus,
  Trash,
  ArrowUp,
  ArrowDown,
  X,
  Package,
  Tag,
  ListChecks,
  FileText,
  ArrowLeft,
  FloppyDisk,
  CaretDown,
  CaretRight,
  Warning,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AssetType,
  AssetScope,
  FieldDefinition,
  FieldType,
  FieldMetadata,
  CreateAssetCategoryDto,
  UpdateAssetCategoryDto,
  RequirementType,
} from 'shared/types/asset';
import {
  useCreateAssetCategory,
  useUpdateAssetCategory,
  useCreateFieldDefinition,
  useUpdateFieldDefinition,
  useCreateAssetType,
  useUpdateAssetType,
  useAssetCategory,
  useCreateTypeFieldDefinition,
  useUpdateTypeFieldDefinition,
  useDeleteFieldDefinition,
  useDeleteTypeFieldDefinition,
  useCreateFileRequirement,
  useUpdateFileRequirement,
  useFileLabels,
} from 'hooks/useAssets';
import { UniversalPickerModal, DatabaseFileLabel } from 'shared/components/UniversalPickerModal';
import { PageInfoBanner } from '@/shared/components/walkthrough';
import { MaintenanceTemplateSection } from './components/MaintenanceTemplateSection';
import ChildRecordTypesSection from './components/ChildRecordTypesSection';
import { SupplierMappingSection } from './components/SupplierMappingSection';
import { IconPicker } from '@/components/dueDiligence/IconPicker/IconPicker';
import { getIconByValue } from '@/components/dueDiligence/IconPicker/icon-categories';

interface FileRequirementItem {
  id?: number;
  assetTypeId?: number;
  fileLabelId?: number;
  fileLabel?: {
    id: number;
    label: string;
  };
  requirementType: 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL';
  description?: string;
  scopes?: string[] | null;
  active: boolean;
  displayOrder: number;
}

interface TypeFieldDefinitionItem {
  id?: number;
  assetTypeId?: number;
  fieldName: string;
  displayName: string;
  fieldType: FieldType;
  isRequired: boolean;
  displayOrder: number;
  enumValues?: string[];
  metadata?: FieldMetadata;
  active: boolean;
  showInTable?: boolean;
}

// Extended type for local state management
// Using Omit to override the fieldDefinitions and fileRequirements types from AssetType
interface AssetTypeWithDetails extends Omit<AssetType, 'fieldDefinitions' | 'fileRequirements'> {
  fieldDefinitions?: TypeFieldDefinitionItem[];
  fileRequirements?: FileRequirementItem[];
}

const AssetCategoryEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId } = useParams<{ categoryId?: string }>();
  const isEditMode = !!categoryId;
  const returnTo = (location.state as { returnTo?: string })?.returnTo || '/settings?tab=voorzieningencategorieen';

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    displayOrder: 0,
    active: true,
    slug: '' as string,
  });
  const [types, setTypes] = useState<AssetTypeWithDetails[]>([]);
  const [fields, setFields] = useState<FieldDefinition[]>([]); // Shared fields at category level
  const [expandedTypes, setExpandedTypes] = useState<Set<number>>(new Set());
  const [originalFieldIds, setOriginalFieldIds] = useState<number[]>([]);
  const [originalTypeIds, setOriginalTypeIds] = useState<number[]>([]);
  // State for override detail modal (view/search/remove overrides)
  const [overrideDetailTarget, setOverrideDetailTarget] = useState<{
    typeIndex: number;
    listKey: string;
    isExcludeMode: boolean;
    scopeKey: string;
    items: any[];
  } | null>(null);
  const [overrideSearch, setOverrideSearch] = useState('');

  // State for scope override picker (building/unit selection)
  const [scopePickerTarget, setScopePickerTarget] = useState<{
    typeIndex: number;
    mode: 'building' | 'unit';
    field: 'excludeBuildingIds' | 'excludeUnitIds' | 'includeBuildingIds' | 'includeUnitIds';
  } | null>(null);

  // State for file label picker modal
  const [fileLabelPickerTarget, setFileLabelPickerTarget] = useState<{
    typeIndex: number;
    reqIndex: number;
    typeScopes: AssetScope[];
  } | null>(null);
  const [originalTypeFieldIds, setOriginalTypeFieldIds] = useState<Record<number, number[]>>({});
  const [originalTypeRequirementIds, setOriginalTypeRequirementIds] = useState<Record<number, number[]>>({});
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  // Unsaved maintenance template changes tracking
  const [dirtyTemplateTypeIds, setDirtyTemplateTypeIds] = useState<Set<number>>(new Set());
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const hasUnsavedTemplateChanges = dirtyTemplateTypeIds.size > 0;
  const historyPushedRef = useRef(false);

  const handleTemplateDirtyChange = useCallback((typeId: number, isDirty: boolean) => {
    setDirtyTemplateTypeIds((prev) => {
      const next = new Set(prev);
      if (isDirty) {
        next.add(typeId);
      } else {
        next.delete(typeId);
      }
      return next;
    });
  }, []);

  const attemptNavigateBack = useCallback(() => {
    if (hasUnsavedTemplateChanges) {
      setShowUnsavedDialog(true);
    } else {
      navigate(returnTo);
    }
  }, [hasUnsavedTemplateChanges, navigate, returnTo]);

  const handleDiscardAndLeave = useCallback(() => {
    setShowUnsavedDialog(false);
    setDirtyTemplateTypeIds(new Set());
    historyPushedRef.current = false;
    // Navigate away — use setTimeout to let state settle and avoid popstate re-trigger
    setTimeout(() => navigate(returnTo), 0);
  }, [navigate, returnTo]);

  // Browser back button / popstate guard
  useEffect(() => {
    if (!hasUnsavedTemplateChanges) {
      historyPushedRef.current = false;
      return;
    }

    // Push a sentinel history entry so browser back hits our handler
    if (!historyPushedRef.current) {
      window.history.pushState({ unsavedGuard: true }, '', window.location.href);
      historyPushedRef.current = true;
    }

    const handlePopState = () => {
      // Re-push to prevent actual navigation
      window.history.pushState({ unsavedGuard: true }, '', window.location.href);
      setShowUnsavedDialog(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedTemplateChanges]);

  // Browser tab/window close guard
  useEffect(() => {
    if (!hasUnsavedTemplateChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedTemplateChanges]);

  // Hooks
  const { createCategory, isCreating } = useCreateAssetCategory();
  const { updateCategory, isUpdating } = useUpdateAssetCategory();
  const { createFieldDefinition } = useCreateFieldDefinition();
  const { updateFieldDefinition } = useUpdateFieldDefinition();
  const { deleteFieldDefinition } = useDeleteFieldDefinition();
  const { createAssetType } = useCreateAssetType();
  const { updateAssetType } = useUpdateAssetType();
  const { createTypeFieldDefinition } = useCreateTypeFieldDefinition();
  const { updateTypeFieldDefinition } = useUpdateTypeFieldDefinition();
  const { deleteTypeFieldDefinition } = useDeleteTypeFieldDefinition();
  const { createFileRequirement } = useCreateFileRequirement();
  const { updateFileRequirement } = useUpdateFileRequirement();
  const { fileLabels, isLoading: isLoadingLabels } = useFileLabels();

  const { category: fetchedCategory, isLoading: isFetchingCategory } = useAssetCategory(
    isEditMode ? categoryId : ''
  );

  const isLoading = isCreating || isUpdating;
  const isInitialLoading = isEditMode && isFetchingCategory;

  // Initialize form data — only once when data first loads.
  // Guard with isFormInitialized to prevent tab-switch refetches from overwriting user edits.
  useEffect(() => {
    if (fetchedCategory && isEditMode && !isFormInitialized) {
      setFormData({
        name: fetchedCategory.name,
        description: fetchedCategory.description || '',
        icon: fetchedCategory.icon || '',
        displayOrder: fetchedCategory.displayOrder,
        active: fetchedCategory.active,
        slug: (fetchedCategory as any).slug || '',
      });
      // Shared fields at category level
      setFields(
        (fetchedCategory.fieldDefinitions || []).map((field) => ({
          id: field.id?.toString(),
          assetCategoryId: field.assetCategoryId?.toString(),
          fieldName: field.fieldName,
          displayName: field.displayName,
          fieldType: field.fieldType,
          isRequired: field.isRequired,
          displayOrder: field.displayOrder,
          validationRules: field.validationRules,
          enumValues: field.enumValues,
          metadata: field.metadata,
          fileLogConnection: field.fileLogConnection,
          enumTypeMapping: field.enumTypeMapping || null,
          showInTable: field.showInTable,
          isActive: field.active,
        }))
      );
      // Types with their field definitions and file requirements
      const typesWithDetails: AssetTypeWithDetails[] = (fetchedCategory.assetTypes || []).map((type) => ({
        ...type,
        fieldDefinitions: (type.fieldDefinitions || []).map((field, index) => ({
          id: field.id,
          assetTypeId: field.assetTypeId,
          fieldName: field.fieldName,
          displayName: field.displayName,
          fieldType: field.fieldType,
          isRequired: field.isRequired,
          displayOrder: field.displayOrder ?? index,
          enumValues: field.enumValues,
          validationRules: field.validationRules,
          metadata: field.metadata,
          fileLogConnection: field.fileLogConnection,
          showInTable: field.showInTable,
          active: field.active,
        })),
        fileRequirements: (type.fileRequirements || []).map((req, index) => ({
          id: typeof req.id === 'string' ? parseInt(req.id) : req.id,
          assetTypeId: req.assetTypeId,
          fileLabelId: typeof req.fileLabelId === 'string' ? parseInt(req.fileLabelId) : (req.fileLabelId as unknown as number),
          fileLabel: req.fileLabel
            ? { id: typeof req.fileLabel.id === 'string' ? parseInt(req.fileLabel.id) : (req.fileLabel.id as unknown as number), label: req.fileLabel.label }
            : undefined,
          requirementType: req.requirementType as 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL',
          description: req.description,
          scopes: (req as any).scopes || null,
          active: req.active,
          displayOrder: index,
        })),
      }));
      setTypes(typesWithDetails);

      setOriginalFieldIds(
        (fetchedCategory.fieldDefinitions || [])
          .map((f) => f.id)
          .filter((id): id is number => id !== undefined)
      );
      setOriginalTypeIds(
        (fetchedCategory.assetTypes || []).map((t) => t.id).filter((id): id is number => id !== undefined)
      );

      // Track original type field IDs and requirement IDs
      const typeFieldIds: Record<number, number[]> = {};
      const typeRequirementIds: Record<number, number[]> = {};
      (fetchedCategory.assetTypes || []).forEach((type) => {
        typeFieldIds[type.id] = (type.fieldDefinitions || [])
          .map((f) => f.id)
          .filter((id): id is number => id !== undefined);
        typeRequirementIds[type.id] = (type.fileRequirements || [])
          .map((r) => typeof r.id === 'string' ? parseInt(r.id) : (r.id as unknown as number))
          .filter((id) => !isNaN(id));
      });
      setOriginalTypeFieldIds(typeFieldIds);
      setOriginalTypeRequirementIds(typeRequirementIds);
      setIsFormInitialized(true);
    }
  }, [fetchedCategory, isEditMode, isFormInitialized]);

  // Scope options
  const scopeOptions = [
    { value: AssetScope.BUILDING, label: 'Gebouw' },
    { value: AssetScope.UNIT, label: 'Unit' },
    { value: AssetScope.ROOM, label: 'Kamer' },
    { value: AssetScope.PORTABLE, label: 'Draagbaar' },
  ];

  // Field type options
  const fieldTypes = [
    { value: FieldType.STRING, label: 'Tekst' },
    { value: FieldType.NUMBER, label: 'Getal' },
    { value: FieldType.BOOLEAN, label: 'Ja/Nee' },
    { value: FieldType.DATE, label: 'Datum' },
    { value: FieldType.ENUM, label: 'Dropdown' },
    { value: FieldType.MULTI_ENUM, label: 'Meerkeuze' },
    { value: FieldType.COMPANY_REFERENCE, label: 'Bedrijf (CRM)' },
  ];

  // Requirement type options
  const requirementTypes = [
    { value: 'REQUIRED', label: 'Verplicht' },
    { value: 'RECOMMENDED', label: 'Aanbevolen' },
    { value: 'OPTIONAL', label: 'Optioneel' },
  ];

  // Type handlers
  const handleAddType = () => {
    const newTypeId = Date.now();
    const newType: AssetTypeWithDetails = {
      id: newTypeId,
      assetCategoryId: 0,
      name: '',
      description: '',
      icon: '',
      scopes: [],
      displayOrder: types.length,
      active: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      fieldDefinitions: [],
      fileRequirements: [],
    };
    setTypes([...types, newType]);
    // Auto-expand the new type
    setExpandedTypes((prev) => new Set([...prev, newTypeId]));
  };

  const handleUpdateType = (index: number, updates: Partial<AssetTypeWithDetails>) => {
    const newTypes = [...types];
    newTypes[index] = { ...newTypes[index], ...updates };
    setTypes(newTypes);
  };

  const handleRemoveType = (index: number) => {
    const typeId = types[index].id;
    setTypes(types.filter((_, i) => i !== index));
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      next.delete(typeId);
      return next;
    });
  };

  const handleMoveType = (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === types.length - 1)) {
      return;
    }
    const newTypes = [...types];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newTypes[index], newTypes[targetIndex]] = [newTypes[targetIndex], newTypes[index]];
    newTypes.forEach((type, i) => (type.displayOrder = i));
    setTypes(newTypes);
  };

  const toggleTypeExpanded = (typeId: number) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(typeId)) {
        next.delete(typeId);
      } else {
        next.add(typeId);
      }
      return next;
    });
  };

  // Type-level field handlers
  const handleAddTypeField = (typeIndex: number) => {
    const newTypes = [...types];
    const type = newTypes[typeIndex];
    const newField: TypeFieldDefinitionItem = {
      fieldName: '',
      displayName: '',
      fieldType: FieldType.STRING,
      isRequired: false,
      displayOrder: (type.fieldDefinitions || []).length,
      metadata: {},
      active: true,
    };
    type.fieldDefinitions = [...(type.fieldDefinitions || []), newField];
    setTypes(newTypes);
  };

  const handleUpdateTypeField = (typeIndex: number, fieldIndex: number, updates: Partial<TypeFieldDefinitionItem>) => {
    const newTypes = [...types];
    const fields = newTypes[typeIndex].fieldDefinitions || [];
    fields[fieldIndex] = { ...fields[fieldIndex], ...updates };
    newTypes[typeIndex].fieldDefinitions = fields;
    setTypes(newTypes);
  };

  const handleRemoveTypeField = (typeIndex: number, fieldIndex: number) => {
    const newTypes = [...types];
    newTypes[typeIndex].fieldDefinitions = (newTypes[typeIndex].fieldDefinitions || []).filter((_, i) => i !== fieldIndex);
    setTypes(newTypes);
  };

  // Type-level enum value handlers
  const handleAddTypeEnumValue = (typeIndex: number, fieldIndex: number) => {
    const newTypes = [...types];
    const field = newTypes[typeIndex].fieldDefinitions?.[fieldIndex];
    if (field) {
      field.enumValues = [...(field.enumValues || []), ''];
      setTypes(newTypes);
    }
  };

  const handleUpdateTypeEnumValue = (typeIndex: number, fieldIndex: number, enumIndex: number, value: string) => {
    const newTypes = [...types];
    const field = newTypes[typeIndex].fieldDefinitions?.[fieldIndex];
    if (field && field.enumValues) {
      field.enumValues[enumIndex] = value;
      setTypes(newTypes);
    }
  };

  const handleRemoveTypeEnumValue = (typeIndex: number, fieldIndex: number, enumIndex: number) => {
    const newTypes = [...types];
    const field = newTypes[typeIndex].fieldDefinitions?.[fieldIndex];
    if (field && field.enumValues) {
      field.enumValues = field.enumValues.filter((_, i) => i !== enumIndex);
      setTypes(newTypes);
    }
  };

  // Type-level file requirement handlers
  const handleAddTypeRequirement = (typeIndex: number) => {
    const newTypes = [...types];
    const type = newTypes[typeIndex];
    const newRequirement: FileRequirementItem = {
      assetTypeId: type.id,
      requirementType: 'REQUIRED',
      description: '',
      active: true,
      displayOrder: (type.fileRequirements || []).length,
    };
    type.fileRequirements = [...(type.fileRequirements || []), newRequirement];
    setTypes(newTypes);
  };

  const handleUpdateTypeRequirement = (typeIndex: number, reqIndex: number, updates: Partial<FileRequirementItem>) => {
    setTypes(prev => prev.map((type, i) => {
      if (i !== typeIndex) return type;
      const requirements = (type.fileRequirements || []).map((req, j) =>
        j === reqIndex ? { ...req, ...updates } : req
      );
      return { ...type, fileRequirements: requirements };
    }));
  };

  const handleRemoveTypeRequirement = (typeIndex: number, reqIndex: number) => {
    const newTypes = [...types];
    newTypes[typeIndex].fileRequirements = (newTypes[typeIndex].fileRequirements || []).filter((_, i) => i !== reqIndex);
    setTypes(newTypes);
  };

  const handleTypeFileLabelChange = (typeIndex: number, reqIndex: number, fileLabelId: string) => {
    const id = parseInt(fileLabelId);
    const selectedLabel = fileLabels.find((label) => label.id === id);
    handleUpdateTypeRequirement(typeIndex, reqIndex, {
      fileLabelId: id,
      fileLabel: selectedLabel ? { id: selectedLabel.id, label: selectedLabel.label } : undefined,
    });
  };

  // Field handlers
  const handleAddField = () => {
    const tempId = `temp_${Date.now()}`;
    const newField: FieldDefinition = {
      id: tempId, // Use as stable key
      fieldName: '',
      displayName: '',
      fieldType: FieldType.STRING,
      displayOrder: fields.length,
      isRequired: false,
      validationRules: {},
      metadata: {},
    };
    setFields([...fields, newField]);
  };

  const handleUpdateField = (index: number, updates: Partial<FieldDefinition>) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFields(newFields);
  };

  const handleRemoveField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === fields.length - 1)) {
      return;
    }
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    newFields.forEach((field, i) => (field.displayOrder = i));
    setFields(newFields);
  };

  // Enum value handlers
  const handleAddEnumValue = (fieldIndex: number) => {
    const field = fields[fieldIndex];
    const newEnumValues = [...(field.enumValues || []), ''];
    handleUpdateField(fieldIndex, { enumValues: newEnumValues });
  };

  const handleUpdateEnumValue = (fieldIndex: number, enumIndex: number, value: string) => {
    const field = fields[fieldIndex];
    const newEnumValues = [...(field.enumValues || [])];
    newEnumValues[enumIndex] = value;
    handleUpdateField(fieldIndex, { enumValues: newEnumValues });
  };

  const handleRemoveEnumValue = (fieldIndex: number, enumIndex: number) => {
    const field = fields[fieldIndex];
    const newEnumValues = (field.enumValues || []).filter((_, i) => i !== enumIndex);
    handleUpdateField(fieldIndex, { enumValues: newEnumValues });
  };

  
  // Submit handler
  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        toast.error('Voer een categorienaam in');
        return;
      }

      if (!isEditMode) {
        // Create mode
        const categoryDto: CreateAssetCategoryDto = {
          name: formData.name,
          description: formData.description || undefined,
          icon: formData.icon || undefined,
          displayOrder: formData.displayOrder,
        };
        const createdCategory = await createCategory(categoryDto);

        // Save shared fields (category level)
        for (const field of fields) {
          if (field.displayName.trim()) {
            await createFieldDefinition({
              assetCategoryId: createdCategory.id.toString(),
              fieldName: field.fieldName,
              displayName: field.displayName,
              fieldType: field.fieldType,
              isRequired: field.isRequired,
              displayOrder: field.displayOrder,
              validationRules: field.validationRules,
              enumValues: field.enumValues,
              metadata: field.metadata,
              showInTable: field.showInTable,
            });
          }
        }

        // Save types with their fields and requirements
        for (const type of types) {
          if (type.name.trim()) {
            const createdType = await createAssetType({
              assetCategoryId: createdCategory.id,
              name: type.name,
              description: type.description,
              icon: type.icon || undefined,
              scopes: type.scopes,
              displayOrder: type.displayOrder,
              scopeRequirements: type.scopeRequirements || null,
              scopeOverrides: (type.scopeOverrides || null) as any,
            });

            // Save type-specific fields
            for (const field of type.fieldDefinitions || []) {
              if (field.displayName.trim()) {
                await createTypeFieldDefinition(
                  createdCategory.id.toString(),
                  createdType.id.toString(),
                  {
                    fieldName: field.fieldName,
                    displayName: field.displayName,
                    fieldType: field.fieldType,
                    isRequired: field.isRequired,
                    displayOrder: field.displayOrder,
                    enumValues: field.enumValues,
                    showInTable: field.showInTable,
                  }
                );
              }
            }

            // Save type-specific file requirements
            for (const requirement of type.fileRequirements || []) {
              if (requirement.fileLabelId) {
                await createFileRequirement({
                  assetTypeId: createdType.id,
                  fileLabelId: requirement.fileLabelId.toString(),
                  requirementType: requirement.requirementType as RequirementType,
                  description: requirement.description,
                  scopes: requirement.scopes as any || null,
                });
              }
            }
          }
        }

        toast.success('Voorziening categorie succesvol aangemaakt');
      } else {
        // Update mode
        const isSysManaged = fetchedCategory?.isSystemManaged === true;

        if (!isSysManaged) {
          // Category settings, fields, and types are locked for system-managed categories
          const categoryDto: UpdateAssetCategoryDto = {
            name: formData.name,
            description: formData.description,
            icon: formData.icon || undefined,
            displayOrder: formData.displayOrder,
            active: formData.active,
            slug: formData.slug || null,
          };
          await updateCategory(categoryId!, categoryDto);
        }

        // Update shared fields (category level)
        if (!isSysManaged) for (const field of fields) {
          const fieldIdNum = field.id ? parseInt(field.id) : undefined;
          if (fieldIdNum && originalFieldIds.includes(fieldIdNum)) {
            await updateFieldDefinition(categoryId!, field.id!, {
              fieldName: field.fieldName,
              displayName: field.displayName,
              fieldType: field.fieldType,
              isRequired: field.isRequired,
              displayOrder: field.displayOrder,
              validationRules: field.validationRules,
              enumValues: field.enumValues,
              metadata: field.metadata,
              showInTable: field.showInTable,
              enumTypeMapping: field.enumTypeMapping || null,
            });
          } else if (field.displayName.trim()) {
            await createFieldDefinition({
              assetCategoryId: categoryId!,
              fieldName: field.fieldName,
              displayName: field.displayName,
              fieldType: field.fieldType,
              isRequired: field.isRequired,
              displayOrder: field.displayOrder,
              validationRules: field.validationRules,
              enumValues: field.enumValues,
              metadata: field.metadata,
              showInTable: field.showInTable,
              enumTypeMapping: field.enumTypeMapping || null,
            });
          }
        }

        // Deactivate removed category-level fields
        if (!isSysManaged) {
          const currentFieldIds = fields
            .map((f) => (f.id ? parseInt(f.id) : undefined))
            .filter((id): id is number => id !== undefined);
          for (const originalId of originalFieldIds) {
            if (!currentFieldIds.includes(originalId)) {
              await deleteFieldDefinition(categoryId!, originalId.toString());
            }
          }
        }

        // Update types with their fields and requirements
        for (const type of types) {
          let typeId: number;

          if (type.id && originalTypeIds.includes(type.id)) {
            if (!isSysManaged) {
              // Update existing type — skip for system-managed
              await updateAssetType(categoryId!, type.id.toString(), {
                name: type.name,
                description: type.description,
                icon: type.icon || undefined,
                scopes: type.scopes,
                displayOrder: type.displayOrder,
                scopeRequirements: type.scopeRequirements || null,
                scopeOverrides: (type.scopeOverrides || null) as any,
              });
            }
            typeId = type.id;
          } else if (type.name.trim() && !isSysManaged) {
            // Create new type — skip for system-managed
            const createdType = await createAssetType({
              assetCategoryId: parseInt(categoryId!),
              name: type.name,
              description: type.description,
              icon: type.icon || undefined,
              scopes: type.scopes,
              displayOrder: type.displayOrder,
              scopeRequirements: type.scopeRequirements || null,
              scopeOverrides: (type.scopeOverrides || null) as any,
            });
            typeId = createdType.id;
          } else {
            typeId = type.id;
            if (!typeId) continue;
          }

          // Handle type-specific fields — skip for system-managed
          if (!isSysManaged) {
          const originalTypeFields = originalTypeFieldIds[type.id] || [];
          for (const field of type.fieldDefinitions || []) {
            if (field.id && originalTypeFields.includes(field.id)) {
              await updateTypeFieldDefinition(
                categoryId!,
                typeId.toString(),
                field.id.toString(),
                {
                  fieldName: field.fieldName,
                  displayName: field.displayName,
                  fieldType: field.fieldType,
                  isRequired: field.isRequired,
                  displayOrder: field.displayOrder,
                  enumValues: field.enumValues,
                  metadata: field.metadata,
                  showInTable: field.showInTable,
                }
              );
            } else if (field.displayName.trim()) {
              await createTypeFieldDefinition(
                categoryId!,
                typeId.toString(),
                {
                  fieldName: field.fieldName,
                  displayName: field.displayName,
                  fieldType: field.fieldType,
                  isRequired: field.isRequired,
                  displayOrder: field.displayOrder,
                  enumValues: field.enumValues,
                  metadata: field.metadata,
                  showInTable: field.showInTable,
                }
              );
            }
          }

          // Deactivate removed type-specific fields
          const currentTypeFieldIds = (type.fieldDefinitions || [])
            .map((f) => f.id)
            .filter((id): id is number => id !== undefined);
          for (const originalId of originalTypeFields) {
            if (!currentTypeFieldIds.includes(originalId)) {
              await deleteTypeFieldDefinition(categoryId!, typeId.toString(), originalId.toString());
            }
          }
          } // end if (!isSysManaged) for type fields

          // Handle type-specific file requirements (always editable, even for system-managed)
          const originalTypeReqs = originalTypeRequirementIds[type.id] || [];
          for (const requirement of type.fileRequirements || []) {
            if (requirement.id && originalTypeReqs.includes(requirement.id)) {
              await updateFileRequirement(requirement.id.toString(), {
                fileLabelId: requirement.fileLabelId?.toString(),
                requirementType: requirement.requirementType as RequirementType,
                description: requirement.description,
                scopes: requirement.scopes as any ?? null,
                active: requirement.active,
              });
            } else if (requirement.fileLabelId) {
              await createFileRequirement({
                assetTypeId: typeId,
                fileLabelId: requirement.fileLabelId.toString(),
                requirementType: requirement.requirementType as RequirementType,
                description: requirement.description,
                scopes: requirement.scopes as any || null,
              });
            }
          }
        }

        toast.success('Voorziening categorie succesvol bijgewerkt');
      }
    } catch (error) {
      console.error('Error saving asset category:', error);
      toast.error(error instanceof Error ? error.message : 'Kan voorziening categorie niet opslaan');
    }
  };

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto p-6 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky z-10 bg-background border-b shadow-sm" style={{ top: 'var(--header-height, 70px)' }}>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={attemptNavigateBack}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold flex items-center gap-2">
                  <Package className="h-5 w-5 text-primary" />
                  {isEditMode ? 'Categorie Bewerken' : 'Nieuwe Categorie'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isEditMode
                    ? `Bewerk "${fetchedCategory?.name || ''}"`
                    : 'Maak een nieuwe voorziening categorie'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={attemptNavigateBack}
              >
                Annuleren
              </Button>
              <Button onClick={handleSubmit} disabled={isLoading || !formData.name.trim()}>
                <FloppyDisk className="h-4 w-4 mr-2" />
                {isLoading ? 'Bezig...' : isEditMode ? 'Opslaan' : 'Aanmaken'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        <PageInfoBanner pageId="asset-categories" />

        {/* System-managed banner */}
        {fetchedCategory?.isSystemManaged && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-800">Systeembeheerd</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Deze categorie en types worden beheerd door het systeem (bijv. voor externe API-koppelingen).
                Velden, types en categorie-instellingen kunnen niet bewerkt worden. Bestandsvereisten zijn wel aanpasbaar.
              </p>
            </div>
          </div>
        )}

        {/* Section 1: Basic Information */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
              1
            </div>
            <div>
              <h3 className="font-semibold text-base">Basisinformatie</h3>
              <p className="text-sm text-muted-foreground">Naam en beschrijving van de categorie</p>
            </div>
          </div>

          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Categorienaam <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder="bijv. Elektrische Toestellen, Sanitair, HVAC"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="text-base"
                />
                <p className="text-xs text-muted-foreground">
                  Geef een duidelijke naam die de groep voorzieningen beschrijft
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Beschrijving</Label>
                <Textarea
                  id="description"
                  placeholder="Beschrijf waarvoor deze categorie wordt gebruikt..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Navigatie-slug</Label>
                <Input
                  id="slug"
                  placeholder="bijv. meters, liften, brandveiligheid"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  className="text-base"
                />
                <p className="text-xs text-muted-foreground">
                  Optioneel. Wanneer ingevuld verschijnt deze categorie als navigatie-item onder Onderhoud.
                  Gebruik alleen kleine letters, cijfers en streepjes.
                </p>
              </div>

              <IconPicker
                value={formData.icon}
                onChange={(value) => setFormData({ ...formData, icon: value })}
                defaultCategory="real_estate"
              />

              {isEditMode && (
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <Label>Status</Label>
                    <p className="text-xs text-muted-foreground">
                      Inactieve categorieën zijn niet zichtbaar bij nieuwe voorzieningen
                    </p>
                  </div>
                  <Switch
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <Separator />

        {/* Section 2: Types */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                2
              </div>
              <div>
                <h3 className="font-semibold text-base">Voorziening Types</h3>
                <p className="text-sm text-muted-foreground">
                  Specifieke voorzieningen binnen deze categorie
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleAddType}>
              <Plus className="h-4 w-4 mr-1" />
              Type toevoegen
            </Button>
          </div>

          {types.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Tag className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">Nog geen types</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                  Voeg types toe zoals: Oven, Koelkast, Wasmachine, Boiler, etc.
                  Elk type heeft zijn eigen velden en bestandsvereisten.
                </p>
                <Button variant="outline" size="sm" className="mt-4" onClick={handleAddType}>
                  <Plus className="h-4 w-4 mr-1" />
                  Eerste type toevoegen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {types
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((type, typeIndex) => {
                  const isExpanded = expandedTypes.has(type.id);
                  return (
                    <Card key={type.id} className="overflow-hidden">
                      {/* Type Header - Always visible */}
                      <div className="p-4 bg-muted/30">
                        <div className="flex items-start gap-3">
                          <div className="flex flex-col gap-0.5 pt-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleMoveType(typeIndex, 'up')}
                              disabled={typeIndex === 0}
                            >
                              <ArrowUp className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => handleMoveType(typeIndex, 'down')}
                              disabled={typeIndex === types.length - 1}
                            >
                              <ArrowDown className="h-3 w-3" />
                            </Button>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => toggleTypeExpanded(type.id)}
                          >
                            {isExpanded ? (
                              <CaretDown className="h-4 w-4" />
                            ) : (
                              <CaretRight className="h-4 w-4" />
                            )}
                          </Button>

                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <Label className="text-xs mb-1.5 block">Naam</Label>
                                <div className="flex items-center gap-2">
                                  {type.icon && (() => {
                                    const IconComp = getIconByValue(type.icon);
                                    return IconComp ? <IconComp weight="duotone" className="h-5 w-5 text-primary flex-shrink-0" /> : null;
                                  })()}
                                  <Input
                                    placeholder="bijv. Oven, Koelkast"
                                    value={type.name}
                                    onChange={(e) => handleUpdateType(typeIndex, { name: e.target.value })}
                                  />
                                </div>
                              </div>
                              {/* Bereik + per-scope requirement toggles */}
                              <div className="col-span-2">
                                <Label className="text-xs mb-1.5 block">Bereik & Verplichting</Label>
                                <div className="space-y-2 pt-1">
                                  {scopeOptions.map((option) => {
                                    const isEnabled = type.scopes?.includes(option.value) ?? false;
                                    const scopeReqs = type.scopeRequirements || {};
                                    const isReqForScope = scopeReqs[option.value] === 'required';

                                    return (
                                      <div key={option.value} className="flex items-center gap-3">
                                        <label className="flex items-center gap-1.5 text-sm cursor-pointer w-28">
                                          <input
                                            type="checkbox"
                                            checked={isEnabled}
                                            onChange={(e) => {
                                              const current = type.scopes || [];
                                              const updated = e.target.checked
                                                ? [...current, option.value]
                                                : current.filter((s: AssetScope) => s !== option.value);
                                              handleUpdateType(typeIndex, { scopes: updated });
                                            }}
                                            className="rounded border-input"
                                          />
                                          {option.label}
                                        </label>
                                        {isEnabled && (
                                          <div className="flex gap-1">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                handleUpdateType(typeIndex, {
                                                  scopeRequirements: { ...scopeReqs, [option.value]: 'optional' }
                                                });
                                              }}
                                              className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                                                !isReqForScope
                                                  ? 'bg-muted text-foreground border-border font-medium'
                                                  : 'bg-background text-muted-foreground border-border/50 hover:bg-accent'
                                              }`}
                                            >
                                              Optioneel
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                handleUpdateType(typeIndex, {
                                                  scopeRequirements: { ...scopeReqs, [option.value]: 'required' }
                                                });
                                              }}
                                              className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                                                isReqForScope
                                                  ? 'bg-red-600 text-white border-red-600 font-medium'
                                                  : 'bg-background text-muted-foreground border-border/50 hover:bg-accent'
                                              }`}
                                            >
                                              Verplicht
                                            </button>

                                            {/* Inline exclude/include button + count */}
                                            {(() => {
                                              const ov = type.scopeOverrides || {};
                                              const scopeKey = option.value; // 'building' or 'unit'
                                              const isExcludeMode = isReqForScope;
                                              const listKey = isExcludeMode
                                                ? (scopeKey === 'building' ? 'excludeBuildings' : 'excludeUnits')
                                                : (scopeKey === 'building' ? 'includeBuildings' : 'includeUnits');
                                              const items: any[] = (ov as any)[listKey] || [];
                                              const count = items.length;
                                              const pickerMode = scopeKey === 'building' ? 'building' as const : 'unit' as const;

                                              return (
                                                <>
                                                  <button
                                                    type="button"
                                                    onClick={() => setScopePickerTarget({
                                                      typeIndex,
                                                      mode: pickerMode,
                                                      field: listKey as any,
                                                    })}
                                                    className="text-[11px] px-2 py-0.5 rounded border border-border/50 bg-background text-muted-foreground hover:bg-accent transition-colors flex items-center gap-1"
                                                    title={isExcludeMode ? 'Uitzonderingen beheren' : 'Specifieke locaties beheren'}
                                                  >
                                                    {isExcludeMode ? 'Uitsluiten' : 'Toevoegen'}
                                                  </button>

                                                  {/* Count badge — opens detail modal */}
                                                  {count > 0 && (
                                                    <button
                                                      type="button"
                                                      onClick={() => setOverrideDetailTarget({ typeIndex, listKey, isExcludeMode, scopeKey, items })}
                                                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full cursor-pointer hover:opacity-80 ${
                                                        isExcludeMode ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                                                      }`}
                                                      title="Klik om details te bekijken"
                                                    >
                                                      {count}
                                                    </button>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>


                              <div>
                                <Label className="text-xs mb-1.5 block">Beschrijving</Label>
                                <Input
                                  placeholder="Optioneel"
                                  value={type.description || ''}
                                  onChange={(e) => handleUpdateType(typeIndex, { description: e.target.value })}
                                />
                              </div>
                            </div>
                            {isExpanded && (
                              <IconPicker
                                value={type.icon || ''}
                                onChange={(value) => handleUpdateType(typeIndex, { icon: value })}
                              />
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            {(type.fieldDefinitions?.length || 0) > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {type.fieldDefinitions?.length} veld(en)
                              </Badge>
                            )}
                            {(type.fileRequirements?.length || 0) > 0 && (
                              <Badge variant="outline" className="text-xs">
                                {type.fileRequirements?.length} bestand(en)
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleRemoveType(typeIndex)}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Expandable Content */}
                      {isExpanded && (
                        <CardContent className="pt-4 space-y-6">
                          {/* Type-specific Fields */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <ListChecks className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">Type-specifieke velden</span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleAddTypeField(typeIndex)}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Veld
                              </Button>
                            </div>
                            {(type.fieldDefinitions?.length || 0) === 0 ? (
                              <div className="text-center py-4 text-xs text-muted-foreground border border-dashed rounded">
                                Nog geen type-specifieke velden
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {type.fieldDefinitions?.map((field, fieldIndex) => (
                                  <div
                                    key={field.id || `field-${fieldIndex}`}
                                    className="p-2 bg-muted/30 rounded space-y-2"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="flex-1 grid grid-cols-4 gap-2">
                                        <Input
                                          placeholder="Veldnaam"
                                          value={field.displayName}
                                          onChange={(e) =>
                                            handleUpdateTypeField(typeIndex, fieldIndex, {
                                              displayName: e.target.value,
                                              fieldName: e.target.value
                                                .toLowerCase()
                                                .replace(/\s+/g, '_')
                                                .replace(/[^a-z0-9_]/g, ''),
                                            })
                                          }
                                          className="h-8 text-sm"
                                        />
                                        <Select
                                          value={field.fieldType}
                                          onValueChange={(value) =>
                                            handleUpdateTypeField(typeIndex, fieldIndex, {
                                              fieldType: value as FieldType,
                                              // Clear enum values if switching away from enum type
                                              enumValues: (value === FieldType.ENUM || value === FieldType.MULTI_ENUM)
                                                ? field.enumValues
                                                : undefined,
                                            })
                                          }
                                        >
                                          <SelectTrigger className="h-8 text-sm">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {fieldTypes.map((ft) => (
                                              <SelectItem key={ft.value} value={ft.value}>
                                                {ft.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <div className="flex items-center gap-2">
                                          <Switch
                                            checked={field.isRequired}
                                            onCheckedChange={(checked) =>
                                              handleUpdateTypeField(typeIndex, fieldIndex, {
                                                isRequired: checked,
                                              })
                                            }
                                          />
                                          <span className="text-xs">Verplicht</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Switch
                                            checked={field.showInTable || false}
                                            onCheckedChange={(checked) =>
                                              handleUpdateTypeField(typeIndex, fieldIndex, {
                                                showInTable: checked,
                                              })
                                            }
                                          />
                                          <span className="text-xs text-muted-foreground" title="Kolom wordt alleen getoond bij filteren op dit type">In tabel</span>
                                        </div>
                                        <div className="flex items-center gap-1.5" title="Eenheid die achter de waarde wordt getoond, bijv. cm, kg, dB, m²">
                                          <Input
                                            placeholder="Eenheid"
                                            value={field.metadata?.suffix || ''}
                                            onChange={(e) =>
                                              handleUpdateTypeField(typeIndex, fieldIndex, {
                                                metadata: { ...field.metadata, suffix: e.target.value },
                                              })
                                            }
                                            className="h-6 w-16 text-xs"
                                          />
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-destructive"
                                        onClick={() => handleRemoveTypeField(typeIndex, fieldIndex)}
                                      >
                                        <Trash className="h-3 w-3" />
                                      </Button>
                                    </div>

                                    {/* Enum values for Dropdown/Multi-select fields */}
                                    {(field.fieldType === FieldType.ENUM || field.fieldType === FieldType.MULTI_ENUM) && (
                                      <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3">
                                        <div className="flex items-center justify-between mb-2">
                                          <Label className="text-xs font-medium text-muted-foreground">Opties</Label>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 text-xs"
                                            onClick={() => handleAddTypeEnumValue(typeIndex, fieldIndex)}
                                          >
                                            <Plus className="h-3 w-3 mr-1" />
                                            Optie toevoegen
                                          </Button>
                                        </div>
                                        {(field.enumValues?.length || 0) === 0 ? (
                                          <p className="text-xs text-muted-foreground italic py-2">
                                            Voeg opties toe voor dit veld
                                          </p>
                                        ) : (
                                          <div className="space-y-1.5">
                                            {field.enumValues?.map((enumValue, enumIndex) => (
                                              <div
                                                key={enumIndex}
                                                className="flex items-center gap-2 bg-background rounded-md px-3 py-1.5 border group"
                                              >
                                                <span className="text-xs text-muted-foreground/50 tabular-nums w-4 text-right shrink-0">
                                                  {enumIndex + 1}.
                                                </span>
                                                <Input
                                                  value={enumValue}
                                                  onChange={(e) =>
                                                    handleUpdateTypeEnumValue(typeIndex, fieldIndex, enumIndex, e.target.value)
                                                  }
                                                  placeholder="Waarde"
                                                  className="h-6 flex-1 text-sm border-0 bg-transparent p-0 focus-visible:ring-0"
                                                />
                                                <Button
                                                  variant="ghost"
                                                  size="icon"
                                                  className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                                                  onClick={() => handleRemoveTypeEnumValue(typeIndex, fieldIndex, enumIndex)}
                                                >
                                                  <X className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <Separator />

                          {/* Type-specific File Requirements */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">Bestandsvereisten</span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => handleAddTypeRequirement(typeIndex)}
                                disabled={isLoadingLabels}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Bestand
                              </Button>
                            </div>
                            {(type.fileRequirements?.length || 0) === 0 ? (
                              <div className="text-center py-4 text-xs text-muted-foreground border border-dashed rounded">
                                Nog geen bestandsvereisten
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {type.fileRequirements?.map((req, reqIndex) => (
                                  <div
                                    key={req.id || `req-${reqIndex}`}
                                    className="p-2 bg-muted/30 rounded space-y-1.5"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="flex-1 grid grid-cols-3 gap-2">
                                        <Button
                                          variant="outline"
                                          className="h-8 text-sm justify-start font-normal"
                                          onClick={() => setFileLabelPickerTarget({
                                            typeIndex,
                                            reqIndex,
                                            typeScopes: type.scopes || [],
                                          })}
                                          disabled={isLoadingLabels}
                                        >
                                          {req.fileLabel?.label || (
                                            <span className="text-muted-foreground">Selecteer label...</span>
                                          )}
                                        </Button>
                                        <Select
                                          value={req.requirementType}
                                          onValueChange={(value) =>
                                            handleUpdateTypeRequirement(typeIndex, reqIndex, {
                                              requirementType: value as 'REQUIRED' | 'RECOMMENDED' | 'OPTIONAL',
                                            })
                                          }
                                        >
                                          <SelectTrigger className="h-8 text-sm">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {requirementTypes.map((rt) => (
                                              <SelectItem key={rt.value} value={rt.value}>
                                                {rt.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Input
                                          placeholder="Beschrijving"
                                          value={req.description || ''}
                                          onChange={(e) =>
                                            handleUpdateTypeRequirement(typeIndex, reqIndex, {
                                              description: e.target.value,
                                            })
                                          }
                                          className="h-8 text-sm"
                                        />
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-destructive"
                                        onClick={() => handleRemoveTypeRequirement(typeIndex, reqIndex)}
                                      >
                                        <Trash className="h-3 w-3" />
                                      </Button>
                                    </div>
                                    {/* Scope filter for this requirement */}
                                    {(type.scopes?.length || 0) > 1 && (
                                      <div className="flex items-center gap-3 pl-1">
                                        <span className="text-[11px] text-muted-foreground">Bereik:</span>
                                        {(!req.scopes || req.scopes.length === 0) ? (
                                          <button
                                            type="button"
                                            className="text-[11px] text-primary hover:underline"
                                            onClick={() => handleUpdateTypeRequirement(typeIndex, reqIndex, {
                                              scopes: [...(type.scopes || [])],
                                            })}
                                          >
                                            Alle bereiken — klik om te beperken
                                          </button>
                                        ) : (
                                          <>
                                            {(type.scopes || []).map((scope: string) => {
                                              const isChecked = req.scopes?.includes(scope) ?? false;
                                              const scopeLabel = scope === 'building' ? 'Gebouw' : scope === 'unit' ? 'Unit' : scope === 'room' ? 'Ruimte' : scope;
                                              return (
                                                <label key={scope} className="flex items-center gap-1 text-[11px] cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                      const current = req.scopes || [];
                                                      const updated = e.target.checked
                                                        ? [...current, scope]
                                                        : current.filter((s: string) => s !== scope);
                                                      handleUpdateTypeRequirement(typeIndex, reqIndex, {
                                                        scopes: updated.length === 0 ? null : updated,
                                                      });
                                                    }}
                                                    className="rounded border-input"
                                                  />
                                                  {scopeLabel}
                                                </label>
                                              );
                                            })}
                                            <button
                                              type="button"
                                              className="text-[11px] text-muted-foreground hover:text-foreground ml-1"
                                              onClick={() => handleUpdateTypeRequirement(typeIndex, reqIndex, { scopes: null })}
                                            >
                                              (reset)
                                            </button>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      )}

                      {/* Maintenance Templates (per type) */}
                      {isExpanded && type.id > 0 && (
                        <CardContent className="pt-0">
                          <Separator className="mb-4" />
                          <MaintenanceTemplateSection
                            assetTypeId={type.id}
                            onDirtyStateChange={(isDirty) => handleTemplateDirtyChange(type.id, isDirty)}
                          />
                        </CardContent>
                      )}

                      {/* Supplier Mappings (per type) */}
                      {isExpanded && type.id > 0 && (
                        <CardContent className="pt-0">
                          <Separator className="mb-4" />
                          <SupplierMappingSection assetTypeId={type.id} />
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
            </div>
          )}
        </section>

        <Separator />

        {/* Section 3: Shared Fields (Category level) */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                3
              </div>
              <div>
                <h3 className="font-semibold text-base">Gedeelde Velden</h3>
                <p className="text-sm text-muted-foreground">
                  Velden die gelden voor alle types in deze categorie
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleAddField}>
              <Plus className="h-4 w-4 mr-1" />
              Veld toevoegen
            </Button>
          </div>

          {/* Default asset fields (read-only) */}
          <div className="mb-4 rounded-lg border border-dashed bg-muted/20 p-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">Standaard velden (altijd beschikbaar)</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {[
                { name: 'Naam', type: 'Tekst' },
                { name: 'Identificatie', type: 'Tekst' },
                { name: 'Status', type: 'Dropdown' },
                { name: 'Gebouw', type: 'Relatie' },
                { name: 'Unit', type: 'Relatie' },
                { name: 'Ruimte', type: 'Relatie' },
                { name: 'Waar geplaatst', type: 'Tekst' },
                { name: 'Installatiedatum', type: 'Datum' },
                { name: 'Garantie vervalt', type: 'Datum' },
                { name: 'Installateur', type: 'Bedrijf' },
                { name: 'Onderhoudsfirma', type: 'Bedrijf' },
                { name: 'Laatste onderhoud', type: 'Datum' },
                { name: 'Volgend onderhoud', type: 'Datum' },
                { name: 'Notities', type: 'Tekst' },
              ].map(f => (
                <span key={f.name} className="text-xs text-muted-foreground">
                  {f.name} <span className="text-muted-foreground/50">({f.type})</span>
                </span>
              ))}
            </div>
          </div>

          {fields.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <ListChecks className="h-10 w-10 text-muted-foreground/50 mb-3" />
                <p className="text-sm font-medium text-muted-foreground">Geen gedeelde velden</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                  Gedeelde velden worden overgenomen door alle types.
                  Type-specifieke velden kun je per type configureren hierboven.
                </p>
                <Button variant="outline" size="sm" className="mt-4" onClick={handleAddField}>
                  <Plus className="h-4 w-4 mr-1" />
                  Eerste gedeeld veld toevoegen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {fields
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((field, index) => (
                  <Card key={field.id || `field-${index}`} className="group">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col gap-0.5 pt-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveField(index, 'up')}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => handleMoveField(index, 'down')}
                            disabled={index === fields.length - 1}
                          >
                            <ArrowDown className="h-3 w-3" />
                          </Button>
                        </div>

                        <div className="flex-1 space-y-3">
                          {/* Row 1: Veldnaam + Type */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-2">
                              <Label className="text-xs mb-1.5 block">Veldnaam</Label>
                              <Input
                                placeholder="bijv. Serienummer"
                                value={field.displayName}
                                onChange={(e) =>
                                  handleUpdateField(index, {
                                    displayName: e.target.value,
                                    fieldName: e.target.value
                                      .toLowerCase()
                                      .replace(/\s+/g, '_')
                                      .replace(/[^a-z0-9_]/g, ''),
                                  })
                                }
                              />
                            </div>
                            <div>
                              <Label className="text-xs mb-1.5 block">Type</Label>
                              <Select
                                value={field.fieldType}
                                onValueChange={(value) =>
                                  handleUpdateField(index, { fieldType: value as FieldType })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {fieldTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {/* Row 2: Verplicht + In tabel + Eenheid */}
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <Switch
                                id={`required-${index}`}
                                checked={field.isRequired || false}
                                onCheckedChange={(checked) =>
                                  handleUpdateField(index, { isRequired: checked })
                                }
                              />
                              <Label htmlFor={`required-${index}`} className="text-xs">
                                Verplicht
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                id={`showInTable-${index}`}
                                checked={field.showInTable || false}
                                onCheckedChange={(checked) =>
                                  handleUpdateField(index, { showInTable: checked })
                                }
                              />
                              <Label htmlFor={`showInTable-${index}`} className="text-xs" title="Kolom wordt alleen getoond bij filteren op deze categorie">
                                In tabel
                              </Label>
                            </div>
                            <div className="flex items-center gap-2" title="Eenheid die achter de waarde wordt getoond, bijv. cm, kg, dB, m²">
                              <Label className="text-xs text-muted-foreground">Eenheid</Label>
                              <Input
                                placeholder="bijv. cm, kg, dB"
                                value={field.metadata?.suffix || ''}
                                onChange={(e) =>
                                  handleUpdateField(index, {
                                    metadata: { ...field.metadata, suffix: e.target.value },
                                  })
                                }
                                className="h-7 w-28 text-xs"
                              />
                            </div>
                          </div>

                          {/* Enum values */}
                          {(field.fieldType === FieldType.ENUM ||
                            field.fieldType === FieldType.MULTI_ENUM) && (
                            <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3">
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-xs font-medium text-muted-foreground">Opties</Label>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-xs"
                                  onClick={() => handleAddEnumValue(index)}
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Optie toevoegen
                                </Button>
                              </div>
                              {(field.enumValues || []).length === 0 ? (
                                <p className="text-xs text-muted-foreground italic py-2">
                                  Voeg opties toe voor dit veld
                                </p>
                              ) : (
                                <div className="space-y-1.5">
                                  {(field.enumValues || []).map((enumValue, enumIndex) => {
                                    const mapping = field.enumTypeMapping || {};
                                    const assignedTypeIds = mapping[enumValue] || [];
                                    const isAllTypes = assignedTypeIds.length === 0;

                                    return (
                                      <div
                                        key={enumIndex}
                                        className="flex items-center gap-2 bg-background rounded-md px-3 py-1.5 border group"
                                      >
                                        <span className="text-xs text-muted-foreground/50 tabular-nums w-4 text-right shrink-0">
                                          {enumIndex + 1}.
                                        </span>
                                        <Input
                                          value={enumValue}
                                          onChange={(e) =>
                                            handleUpdateEnumValue(index, enumIndex, e.target.value)
                                          }
                                          placeholder="Waarde"
                                          className="h-6 w-40 text-sm border-0 bg-transparent p-0 focus-visible:ring-0"
                                        />
                                        {/* Type assignment badges */}
                                        <div className="flex items-center gap-1 flex-wrap flex-1">
                                          {types.map(t => {
                                            const isAssigned = assignedTypeIds.includes(t.id!);
                                            return (
                                              <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => {
                                                  const newMapping = { ...mapping };
                                                  if (isAssigned) {
                                                    newMapping[enumValue] = assignedTypeIds.filter(id => id !== t.id);
                                                    if (newMapping[enumValue].length === 0) delete newMapping[enumValue];
                                                  } else {
                                                    newMapping[enumValue] = [...assignedTypeIds, t.id!];
                                                  }
                                                  handleUpdateField(index, { enumTypeMapping: Object.keys(newMapping).length > 0 ? newMapping : null });
                                                }}
                                                className={`text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                                                  isAssigned
                                                    ? 'bg-primary text-primary-foreground border-primary'
                                                    : isAllTypes
                                                      ? 'bg-muted/50 text-muted-foreground border-transparent'
                                                      : 'bg-background text-muted-foreground/40 border-border'
                                                }`}
                                                title={isAssigned ? `Verwijder van ${t.name}` : `Toewijzen aan ${t.name}`}
                                              >
                                                {t.name}
                                              </button>
                                            );
                                          })}
                                          {isAllTypes && types.length > 0 && (
                                            <span className="text-[10px] text-muted-foreground italic ml-1">alle types</span>
                                          )}
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
                                          onClick={() => handleRemoveEnumValue(index, enumIndex)}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemoveField(index)}
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}
        </section>

        {/* Section 4: Registratietypes (child records) — only in edit mode */}
        {isEditMode && categoryId && (
          <>
            <Separator />
            <ChildRecordTypesSection
              categoryId={categoryId}
              isSystemManaged={fetchedCategory?.isSystemManaged}
            />
          </>
        )}

        {/* Bottom spacer */}
        <div className="h-8" />
      </div>

      {/* File Label Picker Modal */}
      <UniversalPickerModal
        isOpen={fileLabelPickerTarget !== null}
        onClose={() => setFileLabelPickerTarget(null)}
        mode="file-label"
        title="Selecteer bestandslabel"
        onConfirm={(selectedLabel: DatabaseFileLabel) => {
          if (fileLabelPickerTarget) {
            handleTypeFileLabelChange(
              fileLabelPickerTarget.typeIndex,
              fileLabelPickerTarget.reqIndex,
              selectedLabel.id.toString()
            );
            setFileLabelPickerTarget(null);
          }
        }}
        config={{
          databaseFileLabels: fileLabels
            .filter((label) => {
              // Filter by scope matching
              if (!fileLabelPickerTarget) return true;
              const scopes = fileLabelPickerTarget.typeScopes || [];
              if (scopes.includes(AssetScope.BUILDING)) {
                return label.resourceType === 'building' || !label.resourceType;
              }
              if (scopes.includes(AssetScope.UNIT)) {
                return label.resourceType === 'unit' || !label.resourceType;
              }
              return true;
            })
            .sort((a, b) => a.label.localeCompare(b.label, 'nl')) // Sort alphabetically
            .map((label) => ({
              ...label,
              resourceType: label.resourceType || null,
              documentType: label.documentType || null,
              folders: [], // Add missing property for DatabaseFileLabel compatibility
            })),
          searchPlaceholder: 'Zoek bestandslabel...',
        }}
      />

      {/* Override Detail Modal — searchable list of included/excluded locations */}
      <Dialog open={overrideDetailTarget !== null} onOpenChange={(open) => { if (!open) { setOverrideDetailTarget(null); setOverrideSearch(''); } }}>
        <DialogContent className="sm:max-w-[500px] max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {overrideDetailTarget?.isExcludeMode ? 'Uitgesloten locaties' : 'Verplichte locaties'}
              {' '}({overrideDetailTarget?.items.length || 0})
            </DialogTitle>
          </DialogHeader>
          <div className="px-1">
            <Input
              placeholder="Zoek op ID..."
              value={overrideSearch}
              onChange={(e) => setOverrideSearch(e.target.value)}
              className="h-8 text-sm"
              autoFocus
            />
          </div>
          <div className="flex-1 overflow-y-auto space-y-1.5 min-h-0 px-1 py-2">
            {(() => {
              if (!overrideDetailTarget) return null;
              const filtered = overrideDetailTarget.items.filter((item: any) => {
                const id = (item.id || item).toLowerCase();
                const reason = (item.reason || '').toLowerCase();
                const q = overrideSearch.toLowerCase();
                return !q || id.includes(q) || reason.includes(q);
              });
              if (filtered.length === 0) {
                return <p className="text-sm text-muted-foreground text-center py-4">Geen resultaten</p>;
              }
              return filtered.map((item: any, idx: number) => {
                const itemId = item.id || item;
                const reason = item.reason;
                const isExclude = overrideDetailTarget.isExcludeMode;
                return (
                  <div key={idx} className={`flex flex-col gap-0.5 p-2.5 rounded-lg border ${
                    isExclude ? 'bg-red-50/50 border-red-100' : 'bg-green-50/50 border-green-100'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium flex-1">{itemId}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className={`h-6 text-xs px-2 ${isExclude ? 'text-red-600 hover:text-red-800 hover:bg-red-100' : 'text-green-600 hover:text-green-800 hover:bg-green-100'}`}
                        onClick={() => {
                          const { typeIndex, listKey } = overrideDetailTarget;
                          const current = types[typeIndex].scopeOverrides || {};
                          const list: any[] = (current as any)[listKey] || [];
                          const newList = list.filter((e: any) => (e.id || e) !== itemId);
                          handleUpdateType(typeIndex, {
                            scopeOverrides: { ...current, [listKey]: newList }
                          });
                          // Update local state
                          const newItems = overrideDetailTarget.items.filter((e: any) => (e.id || e) !== itemId);
                          if (newItems.length === 0) {
                            setOverrideDetailTarget(null);
                            setOverrideSearch('');
                          } else {
                            setOverrideDetailTarget({ ...overrideDetailTarget, items: newItems });
                          }
                        }}
                      >
                        Verwijderen
                      </Button>
                    </div>
                    {reason && (
                      <p className="text-xs text-muted-foreground italic">{reason}</p>
                    )}
                  </div>
                );
              });
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOverrideDetailTarget(null); setOverrideSearch(''); }}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scope Override Picker Modal (Building/Unit multi-selection) */}
      <UniversalPickerModal
        isOpen={scopePickerTarget !== null}
        onClose={() => setScopePickerTarget(null)}
        mode={scopePickerTarget?.mode || 'building'}
        title={
          scopePickerTarget?.field.startsWith('exclude')
            ? `${scopePickerTarget?.mode === 'building' ? 'Gebouwen' : 'Units'} uitsluiten van verplichting`
            : `${scopePickerTarget?.mode === 'building' ? 'Gebouwen' : 'Units'} verplicht maken`
        }
        onConfirm={(selectedIds: any) => {
          if (scopePickerTarget) {
            const { typeIndex, field } = scopePickerTarget;
            const currentType = types[typeIndex];
            const current = currentType.scopeOverrides || {};
            const ids = (Array.isArray(selectedIds) ? selectedIds : [selectedIds])
              .map((id: any) => String(id))
              .filter(Boolean);
            // Store as {id, reason} objects, preserving existing reasons
            const existingItems: any[] = (current as any)[field] || [];
            const newItems = ids.map((id: string) => {
              const existing = existingItems.find((e: any) => (e.id || e) === id);
              return existing || { id };
            });
            handleUpdateType(typeIndex, {
              scopeOverrides: { ...current, [field]: newItems }
            });
            setScopePickerTarget(null);
          }
        }}
        config={{
          multiSelect: true,
          defaultValue: scopePickerTarget
            ? ((types[scopePickerTarget.typeIndex]?.scopeOverrides as any)?.[scopePickerTarget.field] || [])
                .map((e: any) => e.id || e)
            : [],
          searchPlaceholder: scopePickerTarget?.mode === 'building'
            ? 'Zoek gebouw...'
            : 'Zoek unit...',
          allowRemoval: false,
        }}
      />

      {/* Unsaved maintenance template changes dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Warning className="h-5 w-5 text-amber-500" />
              Niet-opgeslagen wijzigingen
            </AlertDialogTitle>
            <AlertDialogDescription>
              Er zijn niet-opgeslagen wijzigingen bij onderhoudsregels. Wil je deze
              verwijderen of teruggaan om ze op te slaan?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="destructive"
              onClick={handleDiscardAndLeave}
            >
              Wijzigingen verwijderen
            </Button>
            <Button
              onClick={() => setShowUnsavedDialog(false)}
              className="bg-brick-primary hover:bg-brick-primary/90 text-white"
            >
              Terug naar bewerken
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AssetCategoryEditorPage;
